import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Dynamic grid container that adjusts based on participant count
const GridContainer = styled.div`
  display: grid;
  width: 100%;
  height: calc(100% - 60px); // Subtract the height of the controls
  gap: 10px;
  padding: 10px;
  background-color: #000;
  grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
  grid-template-rows: ${({ rows }) => `repeat(${rows}, 1fr)`};
  grid-auto-flow: dense;
  position: relative;
  z-index: 1;
`;

// Video tile component with participant info
const VideoTile = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #2d2d2d;
  border-radius: 8px;
  overflow: hidden;
  z-index: 1;
  
  &.host {
    grid-column: ${({ isLarge, columns }) => isLarge && columns > 1 ? `span ${Math.min(2, columns)}` : 'span 1'};
    grid-row: ${({ isLarge, rows }) => isLarge && rows > 1 ? `span ${Math.min(2, rows)}` : 'span 1'};
    order: -1; /* Ensure host always appears first in the grid */
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: ${({ isScreenShare }) => isScreenShare ? 'contain' : 'cover'};
  background-color: #000;
  z-index: 1;
`;

// Remove the example code snippet that was causing the errors

const ParticipantInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
`;

const ParticipantName = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const StatusIndicators = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ active }) => active ? '#fff' : '#ff4d4f'};
`;

// Add isScreenSharing prop to the component definition
const VideoGrid = ({ participants, localStream, isHost, isScreenSharing }) => {
  const [layout, setLayout] = useState({ columns: 1, rows: 1 });

  useEffect(() => {
    // Calculate optimal grid layout based on participant count
    const count = participants.length + (localStream ? 1 : 0);
    
    // If screen sharing is active, prioritize a larger display for the host
    if (isScreenSharing && participants.some(p => p.isHost)) {
      // When screen sharing, make the host video larger
      if (count <= 3) {
        setLayout({ columns: 2, rows: 1 });
      } else if (count <= 5) {
        setLayout({ columns: 2, rows: 2 });
      } else {
        setLayout({ columns: 3, rows: Math.ceil(count / 3) });
      }
    } else {
      // Original layout logic when not screen sharing
      if (count <= 1) {
        setLayout({ columns: 1, rows: 1 });
      } else if (count <= 2) {
        setLayout({ columns: 2, rows: 1 });
      } else if (count <= 4) {
        setLayout({ columns: 2, rows: 2 });
      } else if (count <= 6) {
        setLayout({ columns: 3, rows: 2 });
      } else if (count <= 9) {
        setLayout({ columns: 3, rows: 3 });
      } else if (count <= 12) {
        setLayout({ columns: 4, rows: 3 });
      } else {
        setLayout({ columns: 4, rows: Math.ceil(count / 4) });
      }
    }
  }, [participants, localStream, isScreenSharing]);

  // Determine if host should be displayed larger
  // Make host larger when screen sharing is active
  const shouldHostBeLarge = (isHost || isScreenSharing) && participants.length > 0;

  return (
    <GridContainer columns={layout.columns} rows={layout.rows}>
      {/* Local stream (self view) */}
      {localStream && (
        <VideoTile 
          className={isHost ? 'host' : ''} 
          isLarge={isHost && shouldHostBeLarge}
          columns={layout.columns}
          rows={layout.rows}
        >
          <Video 
            autoPlay 
            playsInline 
            muted={true} // Always mute self view
            ref={video => {
              if (video && !video.srcObject) {
                video.srcObject = localStream;
              }
            }}
          />
          <ParticipantInfo>
            <ParticipantName>{isHost ? 'You (Host)' : 'You'}</ParticipantName>
            <StatusIndicators>
              <StatusIcon active={localStream.getAudioTracks()[0]?.enabled}>
                {localStream.getAudioTracks()[0]?.enabled ? '🎤' : '🔇'}
              </StatusIcon>
              <StatusIcon active={localStream.getVideoTracks()[0]?.enabled}>
                {localStream.getVideoTracks()[0]?.enabled ? '📹' : '🚫'}
              </StatusIcon>
            </StatusIndicators>
          </ParticipantInfo>
        </VideoTile>
      )}

      {/* Remote participant streams */}
      {participants.map(participant => (
        <VideoTile key={participant.id}>
          <Video 
            autoPlay 
            playsInline 
            ref={video => {
              if (video && participant.stream) {
                // Add logging
                console.log('Setting video srcObject for participant:', participant.username, {
                  hasStream: !!participant.stream,
                  videoTracks: participant.stream?.getVideoTracks().length,
                  videoEnabled: participant.stream?.getVideoTracks()[0]?.enabled
                });
                
                // Only set srcObject if it's different to avoid unnecessary reloads
                if (video.srcObject !== participant.stream) {
                  video.srcObject = participant.stream;
                }
              }
            }}
          />
          <ParticipantInfo>
            <ParticipantName>
              {participant.username} {participant.isHost ? '(Host)' : ''}
            </ParticipantName>
            <StatusIndicators>
              <StatusIcon active={!participant.audioMuted}>
                {!participant.audioMuted ? '🎤' : '🔇'}
              </StatusIcon>
              <StatusIcon active={!participant.videoMuted}>
                {!participant.videoMuted ? '📹' : '🚫'}
              </StatusIcon>
            </StatusIndicators>
          </ParticipantInfo>
        </VideoTile>
      ))}
    </GridContainer>
  );
};

export default VideoGrid;